import type { ClientWay, OrderStateCurrencyPair } from '@/store/orders/types.ts';
import type { Order, OrderMode } from '@/store/types.ts';
import type { OmsErrors, OmsWarning } from '@/types/common.ts';
import type { ISODate, StringTime } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export type TakeProfitOrder = Order<'TakeProfitOrder', TakeProfitOrderProperties>;

export type TakeProfitOrderProperties = {
  status:
    | {
        type: 'pending';
      }
    | {
        type: 'creating';
      }
    | { type: 'failed'; errorMessage: string }
    | {
        type: 'submitted' | 'editing' | 'cancelled';
        tradeId: string;
      };
  isReadyToSubmit: boolean;
  clockOffset?: number;
  currencyPair?: OrderStateCurrencyPair;
  clientWay: ClientWay;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex;

  // the user can set a limit price or a custom price
  // OMS will update the other price when the user sets one of them
  limitPrice?: number;
  customerPrice?: number;
  commercialMargin?: number;
  expiry: GoodTillCancelled | ExpiryDate;

  // by default, use user preferences emails for order
  // override the default with this field
  notificationEmails?: string[];

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;

  errors?: OmsErrors;
  warnings?: OmsWarning;
};

export type GoodTillCancelled = {
  type: 'GoodTillCancelled';
};

export type ExpiryDate = {
  type: 'ExpiryDate';
  date?: ISODate;
  time?: StringTime;
};

export const mapTakeProfit = (
  product: {
    type: 'TakeProfitOrder';
    clientId?: number;
    properties: TakeProfitOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
) => {
  return {
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    way: product.properties.clientWay,
    notional: product.properties.notional,
    notionalCurrencyIndex: product.properties.notionalCurrencyIndex,
    customerPrice: product.properties.customerPrice,
    margin: product.properties.commercialMargin,
    limitPrice: product.properties.limitPrice,
    expiry: product.properties.expiry,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
    localTimeWithOffSet: 0,
  };
};
